import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';

export const LESSONS_CATEGORIES = [
  { id: 'worked', label: 'What worked', color: '#16a34a' },
  { id: 'didnt', label: "What didn't", color: '#dc2626' },
  { id: 'next-year', label: 'For next year', color: '#2563eb' },
] as const;

export type LessonsCategory = (typeof LESSONS_CATEGORIES)[number]['id'];

interface PageRecord {
  id: string;
  title: string;
  slug: string;
  content: string;
  parentPageId?: string;
  version: number;
}

const PARENT_SLUG = 'lessons-learned';

// Both pages start empty — the PageView already renders the title, so any
// in-content h1 or intro paragraph would duplicate it. The year page fills
// up as entries get appended via the lightbulb modal.
const PARENT_CONTENT = '<p></p>';

function yearSlug(year: number): string {
  return `${year}-lessons-learned`;
}

function yearTitle(year: number): string {
  return `${year} Lessons Learned`;
}

function yearContent(_year: number): string {
  return '<p></p>';
}

function isStatus(err: unknown, status: number): boolean {
  if (err instanceof ApiError) return err.status === status;
  return (
    !!err &&
    typeof err === 'object' &&
    'status' in err &&
    (err as { status: number }).status === status
  );
}

async function getOrCreatePage(opts: {
  slug: string;
  title: string;
  content: string;
  parentPageId?: string;
  evergreen?: boolean;
}): Promise<PageRecord> {
  try {
    const res = await api.get<{ data: PageRecord }>(`/pages/slug/${opts.slug}`);
    return res.data;
  } catch (err) {
    if (!isStatus(err, 404)) throw err;
    try {
      const created = await api.post<{ data: PageRecord }>('/pages', {
        title: opts.title,
        content: opts.content,
        parentPageId: opts.parentPageId,
        evergreen: opts.evergreen,
      });
      return created.data;
    } catch (createErr) {
      if (isStatus(createErr, 409)) {
        // Race: another caller created it. Re-fetch.
        const retry = await api.get<{ data: PageRecord }>(`/pages/slug/${opts.slug}`);
        return retry.data;
      }
      throw createErr;
    }
  }
}

/**
 * Resolve (and create on first call) both the Lessons Learned parent and the
 * year-scoped child. Entries are appended to the year page.
 */
export function useLessonsLearnedPage(year: number | null | undefined) {
  return useQuery({
    queryKey: ['lessons-learned-page', year],
    queryFn: async (): Promise<PageRecord> => {
      if (year === null || year === undefined) {
        throw new Error('Active year is required.');
      }
      const parent = await getOrCreatePage({
        slug: PARENT_SLUG,
        title: 'Lessons Learned',
        content: PARENT_CONTENT,
        evergreen: true,
      });
      return getOrCreatePage({
        slug: yearSlug(year),
        title: yearTitle(year),
        content: yearContent(year),
        parentPageId: parent.id,
      });
    },
    enabled: year !== null && year !== undefined,
    staleTime: 60 * 1000,
  });
}

function escapeHtml(raw: string): string {
  return raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Compact "M.D" format (no leading zeros). Matches the user's "4.22" style. */
function shortDate(d = new Date()): string {
  return `${d.getMonth() + 1}.${d.getDate()}`;
}

/** `@handle` from the author name: first word, lowercased, spaces stripped. */
function authorHandle(name: string | undefined): string {
  if (!name) return '';
  const first = name.trim().split(/\s+/)[0] ?? '';
  const clean = first.toLowerCase().replace(/[^a-z0-9._-]/g, '');
  return clean ? `@${clean}` : '';
}

/** Inline styles for the tiny chiclets inside the wiki HTML. */
function chiclet(text: string, outline: string, fill: string): string {
  const style =
    'display:inline-block;padding:1px 7px;margin-left:4px;' +
    'border:1.5px solid ' +
    outline +
    ';background:' +
    fill +
    ';color:' +
    outline +
    ';border-radius:9999px;font-size:11px;font-weight:600;' +
    'line-height:16px;vertical-align:baseline;white-space:nowrap;';
  return `<span style="${style}">${escapeHtml(text)}</span>`;
}

/**
 * Append a lessons-learned entry to the year page. Entries are inserted
 * after the page's leading h1 so the newest is always at the top.
 */
export function useAddLessonsLearnedEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      page: PageRecord;
      category: LessonsCategory;
      text: string;
      authorName?: string;
    }): Promise<PageRecord> => {
      const categoryMeta =
        LESSONS_CATEGORIES.find((c) => c.id === input.category) ?? LESSONS_CATEGORIES[0];

      const bodyHtml = escapeHtml(input.text.trim()).replace(/\n/g, '<br>');
      const handle = authorHandle(input.authorName);

      // Compact single-line entry: body text with trailing pill-shaped
      // chiclets for category / author / date. Matches the "test @ryan 4.22"
      // shape the user asked for.
      const categoryChip = chiclet(categoryMeta.label, categoryMeta.color, `${categoryMeta.color}1a`);
      const authorChip = handle ? chiclet(handle, '#334155', '#e2e8f0') : '';
      const dateChip = chiclet(shortDate(), '#64748b', '#f1f5f9');

      const entryHtml = `<p>${bodyHtml}${categoryChip}${authorChip}${dateChip}</p>`;

      const existing = input.page.content ?? '';
      const h1Match = existing.match(/<h1[^>]*>.*?<\/h1>/i);
      let nextContent: string;
      if (h1Match && typeof h1Match.index === 'number') {
        const end = h1Match.index + h1Match[0].length;
        nextContent = existing.slice(0, end) + entryHtml + existing.slice(end);
      } else {
        nextContent = entryHtml + existing;
      }

      const res = await api.put<{ data: PageRecord }>(
        `/pages/slug/${input.page.slug}`,
        {
          content: nextContent,
          version: input.page.version,
        },
      );
      return res.data;
    },
    onSuccess: (page) => {
      queryClient.setQueryData(['lessons-learned-page', parseYearFromSlug(page.slug)], page);
      queryClient.invalidateQueries({ queryKey: ['pages'] });
    },
  });
}

function parseYearFromSlug(slug: string): number | null {
  const m = slug.match(/^(\d{4})-lessons-learned$/);
  return m ? Number(m[1]) : null;
}
